import { EnvironmentConfiguration } from '../configuration/web/environment.configuration';
import { environmentDev } from '../configuration/web/environment.dev';
import { environmentVnext } from '../configuration/web/environment.vnext';
import { environmentProd } from '../configuration/web/environment.prod';
import { environmentEdge } from '../configuration/web/environment.edge';
import { environmentReleaseCandidate } from '../configuration/web/environment.release.candidate';
import { environmentHotfix } from '../configuration/web/environment.hotfix';
import { ApplicationEnvironment } from '../entities/catalog/application.environment';
import { ApplicationTokens } from '../entities/catalog/application.tokens';

export enum DomainType {
    Id = 'id',
    Console = 'console',
    Register = 'register',
    Tenant = 'tenant',
    Compliance = 'compliance',
    ClientPortal = 'clients',
    ContractorPortal = 'contractors',
    TrackingPortal = 'tracking',
    PdfViewer = 'pdf-viewer',
    Mobile = 'mobile'
}


export class DomainUtility {

    public static getEnvironmentConfiguration(environment: ApplicationEnvironment = null): EnvironmentConfiguration {

        if (!environment) {
            environment = ApplicationEnvironment.Development;
        }

        switch (environment) {

            case ApplicationEnvironment.Production:
                return environmentProd;

            case ApplicationEnvironment.Hotfix:
                return environmentHotfix;

            case ApplicationEnvironment.Staging:
            case ApplicationEnvironment.ReleaseCandidate:
                return environmentReleaseCandidate;

            case ApplicationEnvironment.Nightly:
            case ApplicationEnvironment.Vnext:
                return environmentVnext;

            case ApplicationEnvironment.Regression:
            case ApplicationEnvironment.Edge:
                return environmentEdge;

            case ApplicationEnvironment.Kind: {
                const result = { ...environmentDev } as EnvironmentConfiguration;
                result.domain = 'workbuddy.kind';
                result.jms.loginHref = 'https://id.workbuddy.kind';
                result.jms.logoutHref = 'https://id.workbuddy.kind/logout';
                return result;
            }

            default:
                return environmentDev;
        }
    }

    public static getFriendlyEnvironment(environment: ApplicationEnvironment = null): string {

        switch (environment) {

            case ApplicationEnvironment.Production:
                return 'Production';

            case ApplicationEnvironment.Staging:
            case ApplicationEnvironment.ReleaseCandidate:
                return 'Release Candidate';

            case ApplicationEnvironment.Nightly:
            case ApplicationEnvironment.Vnext:
                return 'Nightly';

            case ApplicationEnvironment.Regression:
            case ApplicationEnvironment.Edge:
                return 'Edge';

            default:
                return 'Development';
        }
    }

    public static isProduction(environment: ApplicationEnvironment = null): boolean {
        const env = DomainUtility.getEnvironmentConfiguration(environment);
        return env.production;
    }

    public static getDomain(environment: ApplicationEnvironment): string {

        const env = DomainUtility.getEnvironmentConfiguration(environment);
        return env.domain;
    }

    public static getDatabaseName(application: ApplicationTokens, environment: ApplicationEnvironment, tenant: string = null): string {

        switch (application) {
            case ApplicationTokens.ID:
                return this.replaceAll(`${environment}-id`, '.', '-');

            case ApplicationTokens.Console:
                return this.replaceAll(`${environment}-console`, '.', '-');

            case ApplicationTokens.JMS:
            case ApplicationTokens.MicroservicePricing:
            case ApplicationTokens.MicroserviceConnect:
            case ApplicationTokens.MicroserviceSideEffects:
            case ApplicationTokens.MicroserviceWalCoordinator:
            case ApplicationTokens.CustomisationAssett:
            case ApplicationTokens.CustomisationBgis:
                return this.replaceAll(`${environment}-t-${tenant}`, '.', '-');

            case ApplicationTokens.Compliance:
                return this.replaceAll(`${environment}-compliance`, '.', '-');

            case ApplicationTokens.Portal:
                return this.replaceAll(`${environment}-portal`, '.', '-');
        }
    }

    public static getConnectionString(databaseServer: string, databaseName: string) {
        return databaseServer && databaseServer.toLocaleLowerCase().startsWith('mongodb')
            ? databaseServer
            : `${databaseServer}/${databaseName}`; // ?retryWrites=true
    }

    public static getDomainType(domain: string): DomainType {

        let domainType = null;

        if (domain.startsWith('id.'))
            domainType = DomainType.Id;
        else if (domain.startsWith('register.'))
            domainType = DomainType.Register;
        else if (domain.startsWith('console.'))
            domainType = DomainType.Console;
        else if (domain.startsWith('clients.'))
            domainType = DomainType.ClientPortal;
        else if (domain.startsWith('contractors.'))
            domainType = DomainType.ContractorPortal;
        else if (domain.startsWith('tracking.'))
            domainType = DomainType.TrackingPortal;
        else if (domain.startsWith('compliance.'))
            domainType = DomainType.Compliance;
        else if (domain.startsWith('mobile.'))
            domainType = DomainType.Mobile;
        else if (domain.startsWith('pdf-viewer.'))
            domainType = DomainType.PdfViewer;
        else
            domainType = DomainType.Tenant;

        return domainType;
    }

    public static getApplicationToken(app: string): ApplicationTokens {

        switch (app) {
            case 'id':
                return ApplicationTokens.ID;

            case 'jms':
                return ApplicationTokens.JMS;

            case 'jms-ms-pricing':
                return ApplicationTokens.MicroservicePricing;

            case 'jms-ms-connect':
                return ApplicationTokens.MicroserviceConnect;

            case 'jms-ms-side-effects':
                return ApplicationTokens.MicroserviceSideEffects;

            case 'compliance':
                return ApplicationTokens.Compliance;

            case 'portal':
                return ApplicationTokens.Portal;

            case 'console':
                return ApplicationTokens.Console;

            case 'pdf':
                return ApplicationTokens.PdfWorker;

            case 'global-worker':
                return ApplicationTokens.GlobalWorker;

            case 'functions':
                return ApplicationTokens.Functions;

            case 'functionsZip':
                return ApplicationTokens.FunctionZip;

            case 'functionsCopyBlob':
                return ApplicationTokens.FunctionCopyBlob;

            case 'jms-customisation-assett':
                return ApplicationTokens.CustomisationAssett;

            case 'jms-customisation-bgis':
                return ApplicationTokens.CustomisationBgis;

        }
    }

    private static replaceAll(data: string, find: string, replace: string) {
        return data.split(find).join(replace);
    }
}

export function isPortal(domainType: DomainType): boolean {
    return [DomainType.ClientPortal, DomainType.ContractorPortal].includes(domainType);
}

export function isClientPortal(domainType: DomainType): boolean {
    return [DomainType.ClientPortal].includes(domainType);
}

export function isContractorPortal(domainType: DomainType): boolean {
    return [DomainType.ContractorPortal].includes(domainType);
}

// export function isTenant(domainType: DomainType): boolean {
//     return [DomainType.Tenant].includes(domainType);
// }

export function isConsole(domainType: DomainType): boolean {
    return [DomainType.Console].includes(domainType);
}